import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { friendlyError } from "@/lib/errors";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter,
} from "@/components/ui/drawer";
import { toast } from "sonner";

// FR8 / Story 6.1 AC4: when an agent's prerequisites report missing[] fields,
// resolve them INLINE (never bounce). Each missing entry {scope, field, label}
// becomes a labelled input; saving PATCHes the avatar and/or brand persona, then
// revalidates via GET /agents/{slug}/prerequisites before letting the flow
// continue. Any still-missing fields keep the drawer open.
export default function RequiredFieldsDrawer({
  open, onOpenChange, agentSlug, avatar, missing = [], onResolved,
}) {
  const [fields, setFields] = useState(missing);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setFields(missing);
      setValues({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      const avatarPatch = {};
      const personaPatch = {};
      for (const f of fields) {
        const v = values[f.field];
        if (v == null || String(v).trim() === "") continue;
        if (f.scope === "brand_persona") personaPatch[f.field] = v;
        else avatarPatch[f.field] = v;
      }
      if (Object.keys(avatarPatch).length && avatar?.id) {
        await api.patch(`/avatars/${avatar.id}`, avatarPatch);
      }
      if (Object.keys(personaPatch).length) {
        await api.patch("/persona/me", personaPatch);
      }
      // Revalidate against the server so we never proceed on a stale gate.
      const { data } = await api.get(`/agents/${agentSlug}/prerequisites`);
      const remaining = data.missing || [];
      if (remaining.length) {
        setFields(remaining);
        setValues({});
        toast.error("Ainda faltam alguns campos. Preencha para continuar.");
      } else {
        onResolved?.(avatar);
      }
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setSaving(false);
    }
  };

  const allFilled = fields.every((f) => values[f.field] && String(values[f.field]).trim() !== "");

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent data-testid="required-fields-drawer" className="bg-brand-surface border-brand-line">
        <div className="mx-auto w-full max-w-md">
          <DrawerHeader className="text-left">
            <DrawerTitle className="font-display text-2xl text-brand-ink">
              Faltam alguns dados
            </DrawerTitle>
            <DrawerDescription className="text-brand-ink2">
              Preencha para liberar este agente — sem sair da conversa.
            </DrawerDescription>
          </DrawerHeader>

          <div className="px-4 space-y-4 max-h-[50vh] overflow-y-auto overflow-x-hidden scrollbar-soft">
            {fields.map((f) => (
              <label key={`${f.scope}-${f.field}`} className="block" data-testid={`required-field-${f.field}`}>
                <span className="block text-[11px] tracking-[0.18em] uppercase text-brand-ink2 font-ui mb-1.5">
                  {f.label || f.field}
                </span>
                <input
                  data-testid={`required-input-${f.field}`}
                  value={values[f.field] || ""}
                  onChange={(e) => setValues((v) => ({ ...v, [f.field]: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl border border-brand-line bg-brand-elevated text-white focus:border-brand-rose focus:outline-none text-[16px] placeholder:text-brand-ink3 min-h-[44px]"
                />
              </label>
            ))}
          </div>

          <DrawerFooter>
            <button
              type="button"
              data-testid="required-fields-save"
              onClick={save}
              disabled={saving || !allFilled}
              className="w-full inline-flex items-center justify-center gap-2 bg-brand-rose hover:bg-brand-roseHover text-white px-6 py-3.5 rounded-full font-ui text-[14px] transition-colors min-h-[44px] disabled:opacity-50"
            >
              {saving ? "Salvando…" : "Salvar e continuar"}
            </button>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
